import React from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    IconButton,
    Breadcrumbs,
    Link,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import {
    ArrowBack,
    Security,
    LocationOn,
    Phone,
    Warning,
    CheckCircle,
    ExpandMore,
    Shield,
    Visibility,
    Group,
    LocalHospital,
    Psychology,
    VpnLock
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LIGHT_PURPLE = '#A76FFF';
const VIBRANT_RED = '#FF5A76';

const DatingSafetyScreen: React.FC = () => {
    const navigate = useNavigate();

    const safetyTips = [
        {
            icon: <LocationOn sx={{ color: LIGHT_PURPLE }} />,
            title: 'Meet in Public Places',
            description: 'Always choose well-lit, populated venues for first dates. Avoid private homes or secluded locations.'
        },
        {
            icon: <Phone sx={{ color: LIGHT_PURPLE }} />,
            title: 'Tell Someone Your Plans',
            description: 'Share your date details with a trusted friend or family member, including location and expected return time.'
        },
        {
            icon: <Shield sx={{ color: LIGHT_PURPLE }} />,
            title: 'Use Your Own Transportation',
            description: 'Drive yourself or use your own ride service. Maintain control over your ability to leave if needed.'
        },
        {
            icon: <Visibility sx={{ color: LIGHT_PURPLE }} />,
            title: 'Video Chat First',
            description: 'Video call before meeting in person to verify identity and build initial comfort.'
        },
        {
            icon: <VpnLock sx={{ color: LIGHT_PURPLE }} />,
            title: 'Protect Personal Information',
            description: 'Keep your full name, address, workplace, and financial information private initially.'
        },
        {
            icon: <Psychology sx={{ color: LIGHT_PURPLE }} />,
            title: 'Trust Your Instincts',
            description: 'If something feels wrong, trust your gut feeling and prioritize your safety above politeness.'
        }
    ];

    const redFlags = [
        'Pressures you to drink alcohol or take substances',
        'Becomes aggressive, controlling, or possessive',
        'Disrespects your boundaries or ignores when you say "no"',
        'Asks inappropriate questions about money or personal details',
        'Tries to isolate you from friends, family, or public spaces',
        'Their behavior drastically differs from online interactions',
        'Refuses to meet in public places',
        'Pushes for physical intimacy too quickly',
        'Has inconsistent stories about their life',
        'Shows signs of substance abuse or erratic behavior'
    ];

    const emergencyPlan = [
        {
            step: '1',
            title: 'Create a Check-in Schedule',
            description: 'Arrange specific times to text or call a trusted friend during your date.'
        },
        {
            step: '2',
            title: 'Establish a Code Word',
            description: 'Have a secret word or phrase that signals you need help when talking to your safety contact.'
        },
        {
            step: '3',
            title: 'Share Date Information',
            description: 'Provide your safety contact with your date\'s name, photo, location, and expected timeline.'
        },
        {
            step: '4',
            title: 'Plan Your Exit Strategy',
            description: 'Know how you\'ll leave if needed and have backup transportation options ready.'
        }
    ];

    return (
        <>
            <SEOHead
                title="Dating Safety Guide | Pop The Balloon"
                description="Read practical online and first-date safety guidance, warning signs to watch for and steps for reporting or blocking inappropriate behaviour."
                canonical="https://www.poptheballoonapp.com/dating-safety"
                ogTitle="Essential Dating Safety Guide - Stay Safe While Dating"
                ogDescription="Learn essential dating safety tips, recognize red flags, and create safety plans. Comprehensive guide to protecting yourself while dating."
                ogUrl="https://www.poptheballoonapp.com/dating-safety"
            />

            <SiteHeader />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {/* Breadcrumbs */}
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                    <Link
                        color="inherit"
                        href="/"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/');
                        }}
                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                        Home
                    </Link>
                    <Typography color="text.primary">Dating Safety</Typography>
                </Breadcrumbs>

                {/* Header */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Security sx={{ mr: 2, color: LIGHT_PURPLE, fontSize: 32 }} />
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: LIGHT_PURPLE }}>
                            Dating Safety Guide
                        </Typography>
                    </Box>
                    <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px' }}>
                        Your safety is paramount. Learn essential safety practices, recognize warning signs, and create effective safety plans for dating in the modern world.
                    </Typography>
                </Box>

                {/* Important Safety Alert */}
                <Alert 
                    severity="warning" 
                    icon={<Warning />}
                    sx={{ mb: 4, fontSize: '1.1rem', bgcolor: 'rgba(255, 193, 7, 0.1)' }}
                >
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        Emergency Contact Information
                    </Typography>
                    <Typography sx={{ mb: 1 }}>
                        If you're in immediate danger, call your local emergency number.
                    </Typography>
                    <Typography component="div">
                        <strong>Mauritius:</strong> police 999 or 112, ambulance (SAMU) 114, domestic violence hotline 139 (free, 24/7)
                        <br />
                        <strong>UK:</strong> 999, National Domestic Abuse Helpline 0808 2000 247 (free, 24/7)
                        <br />
                        <strong>US:</strong> 911, National Domestic Violence Hotline 1-800-799-7233 or text START to 88788
                    </Typography>
                </Alert>

                {/* Essential Safety Tips */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <Shield sx={{ mr: 2, color: LIGHT_PURPLE }} />
                        Essential Safety Tips
                    </Typography>
                    <Grid container spacing={3}>
                        {safetyTips.map((tip, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Card sx={{ height: '100%', border: '1px solid rgba(167, 111, 255, 0.2)' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Box sx={{ mt: 0.5 }}>
                                                {tip.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                                    {tip.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    {tip.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Red Flags Section */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <Warning sx={{ mr: 2, color: VIBRANT_RED }} />
                        Dating Red Flags: Warning Signs to Watch For
                    </Typography>
                    <Card sx={{ border: '1px solid rgba(255, 90, 118, 0.2)' }}>
                        <CardContent>
                            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
                                Trust your instincts if your date exhibits any of these warning behaviors:
                            </Typography>
                            <Grid container spacing={2}>
                                {redFlags.map((flag, index) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Warning sx={{ color: VIBRANT_RED, fontSize: 20, mt: 0.2 }} />
                                            <Typography variant="body1">{flag}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            <Alert severity="error" sx={{ mt: 3 }}>
                                <Typography variant="body1">
                                    <strong>Remember:</strong> If any of these red flags appear, prioritize your safety. 
                                    It's better to end a date early than to risk your wellbeing.
                                </Typography>
                            </Alert>
                        </CardContent>
                    </Card>
                </Box>

                {/* Emergency Planning */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <LocalHospital sx={{ mr: 2, color: LIGHT_PURPLE }} />
                        Create Your Safety Plan
                    </Typography>
                    <Grid container spacing={3}>
                        {emergencyPlan.map((plan, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Card sx={{ height: '100%', border: '1px solid rgba(167, 111, 255, 0.2)' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Box sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                bgcolor: LIGHT_PURPLE,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem'
                                            }}>
                                                {plan.step}
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                                    {plan.title}
                                                </Typography>
                                                <Typography variant="body1" color="text.secondary">
                                                    {plan.description}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Detailed Safety Scenarios */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                        Safety Scenarios & Solutions
                    </Typography>
                    
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Online Dating Safety</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                <strong>Profile Safety:</strong> Use recent, unfiltered photos but avoid images that reveal your location, workplace, or home. Don't include your full name in your profile initially.
                            </Typography>
                            <Typography paragraph>
                                <strong>Communication:</strong> Keep conversations within the dating app until you've established trust. Be cautious of anyone who immediately wants to move to personal phone numbers or other platforms.
                            </Typography>
                            <Typography paragraph>
                                <strong>Information Sharing:</strong> Gradually share personal information as trust builds. Never share financial information, social security numbers, or passwords.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">First Date Safety</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                <strong>Location Choice:</strong> Choose public places you're familiar with. Coffee shops, restaurants, and public parks during daylight hours are ideal. Avoid movies (limited conversation) for first dates.
                            </Typography>
                            <Typography paragraph>
                                <strong>Transportation:</strong> Meet at the location rather than being picked up. Keep your car keys easily accessible. If using rideshare, don't share the ride with your date initially.
                            </Typography>
                            <Typography paragraph>
                                <strong>Duration:</strong> Keep first dates relatively short (1-2 hours) to maintain safety and reduce pressure. You can always extend if things go well.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">Digital Privacy & Security</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                <strong>Location Services:</strong> Turn off location sharing in dating apps and on social media. Avoid posting real-time location updates.
                            </Typography>
                            <Typography paragraph>
                                <strong>Social Media:</strong> Review your privacy settings. Consider what information is publicly visible. Be cautious about accepting friend requests from new matches.
                            </Typography>
                            <Typography paragraph>
                                <strong>Photo Safety:</strong> Check photo metadata and consider what's visible in backgrounds. Avoid photos with identifiable landmarks near your home or work.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">What to Do If You Feel Unsafe</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography paragraph>
                                <strong>During a Date:</strong> Trust your instincts immediately. Go to the bathroom and call a friend if you need help. Create an excuse to leave if necessary. Your safety is more important than being polite.
                            </Typography>
                            <Typography paragraph>
                                <strong>Online Harassment:</strong> Block and report users who make you uncomfortable. Save screenshots of threatening messages. Don't engage with aggressive or abusive behavior.
                            </Typography>
                            <Typography paragraph>
                                <strong>Stalking or Threats:</strong> Document all incidents, contact local authorities, and consider getting a restraining order if necessary. Inform trusted friends and family about the situation.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>

                {/* Resources */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                        Safety Resources & Support
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ border: '1px solid rgba(167, 111, 255, 0.2)' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: LIGHT_PURPLE }}>
                                        Emergency Contacts
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Phone sx={{ color: VIBRANT_RED }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Emergency Services"
                                                secondary="Mauritius 999 or 112 / UK 999 / US 911"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Phone sx={{ color: LIGHT_PURPLE }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Domestic Violence Hotline"
                                                secondary="Mauritius 139 / UK 0808 2000 247 / US 1-800-799-7233"
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ border: '1px solid rgba(167, 111, 255, 0.2)' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: LIGHT_PURPLE }}>
                                        Safety Apps
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CheckCircle sx={{ color: 'green' }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="bSafe"
                                                secondary="Personal safety app with GPS tracking"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <CheckCircle sx={{ color: 'green' }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Noonlight"
                                                secondary="Emergency response at the touch of a button"
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card sx={{ border: '1px solid rgba(167, 111, 255, 0.2)' }}>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: LIGHT_PURPLE }}>
                                        Additional Resources
                                    </Typography>
                                    <List dense>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Group sx={{ color: LIGHT_PURPLE }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Local Support Groups"
                                                secondary="Search for dating safety workshops in your area"
                                            />
                                        </ListItem>
                                        <ListItem>
                                            <ListItemIcon>
                                                <Psychology sx={{ color: LIGHT_PURPLE }} />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary="Counseling Services"
                                                secondary="Professional support for dating anxiety or trauma"
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Call to Action */}
                <Card sx={{
                    textAlign: 'center',
                    py: 6,
                    background: `linear-gradient(135deg, rgba(167, 111, 255, 0.1), rgba(255, 90, 118, 0.1))`,
                    border: `1px solid rgba(167, 111, 255, 0.2)`
                }}>
                    <CardContent>
                        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                            Date Safely with Pop The Balloon
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', maxWidth: '600px', mx: 'auto' }}>
                            Our platform prioritizes user safety with verification features, reporting tools, and community guidelines that promote respectful interactions.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate('/register')}
                                sx={{
                                    bgcolor: LIGHT_PURPLE,
                                    px: 4,
                                    py: 2,
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        bgcolor: '#8A5CF5',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(167, 111, 255, 0.4)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Start Safe Dating
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => navigate('/blog')}
                                sx={{
                                    borderColor: LIGHT_PURPLE,
                                    color: LIGHT_PURPLE,
                                    px: 4,
                                    py: 2,
                                    fontSize: '1.1rem',
                                    '&:hover': {
                                        borderColor: '#8A5CF5',
                                        bgcolor: 'rgba(167, 111, 255, 0.1)'
                                    }
                                }}
                            >
                                Read More Tips
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
            <AppFooter />
        </>
    );
};

export default DatingSafetyScreen;